import React from "react";
import {
    Col,
    FormGroup as StrapGroup,
    FormGroupProps as StrapGroupProps,
    Row,
} from "reactstrap";
import { StringHelper } from "../../../../utils/helpers";
import {
    ControlLabelProps,
    IControlStyles,
} from "../../../controls/components";
import { FormLabel } from "../.";

export type FormGroupType =
    | "check"
    | "floating"
    | "fieldRow"
    | "inline"
    | "radio"
    | "row"
    | "switch"
    | "topLabel";

export interface FormGroupProps extends StrapGroupProps {
    controlStyles?: IControlStyles | undefined;
    isLoading?: boolean | undefined;
    groupType?: FormGroupType | undefined;
    label?: string | undefined;
    note?: string;
    isOptional?: boolean | undefined;
    tooltip?: string | undefined;
    isHidden?: boolean | undefined;
    labelMd?: number | undefined;
    controlMd?: number | undefined;
    labelWidth?: string | undefined;
    required?: boolean | undefined;
}

const ComponentProps = (
    props: FormGroupProps,
    groupType: FormGroupType
): StrapGroupProps => {
    const componentProps = Object.assign({}, props);
    delete componentProps.controlStyles;
    delete componentProps.isLoading;
    delete componentProps.groupType;
    delete componentProps.isHidden;
    delete componentProps.label;
    delete componentProps.note;
    delete componentProps.isOptional;
    delete componentProps.tooltip;
    delete componentProps.labelMd;
    delete componentProps.controlMd;
    delete componentProps.labelWidth;
    delete componentProps.required;

    componentProps.check = groupType === "check" || groupType === "radio";
    componentProps.floating = groupType === "floating";
    componentProps.inline = groupType === "inline";
    componentProps.row = groupType === "row";
    componentProps.switch = groupType === "switch";

    return componentProps;
};

const LabelProps = (props: FormGroupProps): ControlLabelProps => {
    return {
        controlStyles: props.controlStyles,
        isOptional: props.isOptional,
        tooltip: props.tooltip,
        required: props.required,
    };
};

function GetGroupType(props: FormGroupProps): FormGroupType {
    if (props.check) {
        return "check";
    }

    if (props.floating) {
        return "floating";
    }

    if (props.inline) {
        return "inline";
    }

    if (props.row) {
        return "row";
    }

    if (props.switch) {
        return "switch";
    }

    return props.groupType ?? "fieldRow";
}

export const FormGroup: React.FC<FormGroupProps> = (props) => {
    const groupType = GetGroupType(props);
    const hasLabel =
        StringHelper.isNotNullOrEmpty(props.label) && !(props.isHidden ?? false);

    if (props.isLoading ?? false) {
        return (
            <p className="placeholder-glow">
                <span className="placeholder col-12 placeholder-lg bg-secondary"></span>
            </p>
        );
    }

    if (groupType === "fieldRow") {
        const labelMd = props.labelMd ?? 3;
        const controlMd = props.controlMd ?? (hasLabel ? 12 - labelMd : 12);

        if (props.labelWidth) {
            return (
                <StrapGroup {...ComponentProps(props, groupType)}>
                    <Row className="align-items-start">
                        {hasLabel && (
                            <Col
                                xs={12}
                                md="auto"
                                style={{
                                    flex: `0 0 ${props.labelWidth}`,
                                    maxWidth: props.labelWidth,
                                }}
                            >
                                <FormLabel
                                    {...LabelProps(props)}
                                    style={{
                                        marginBottom: "var(--space-tight-padding)",
                                        paddingTop: "var(--space-tight-padding)",
                                    }}
                                >
                                    {props.label}
                                </FormLabel>
                            </Col>
                        )}
                        <Col xs={12} md>
                            {props.children}
                            {props.note && (
                                <div className="small d-flex fst-italic opacity-75 mt-1">
                                    <span className="me-2 text-primary">*</span>
                                    <span>{props.note}</span>
                                </div>
                            )}
                        </Col>
                    </Row>
                </StrapGroup>
            );
        }

        return (
            <StrapGroup {...ComponentProps(props, groupType)}>
                <Row className="align-items-start">
                    {hasLabel && (
                        <Col xs={12} md={labelMd}>
                            <FormLabel
                                {...LabelProps(props)}
                                style={{
                                    marginBottom: "var(--space-tight-padding)",
                                    paddingTop: "var(--space-tight-padding)",
                                }}
                            >
                                {props.label}
                            </FormLabel>
                        </Col>
                    )}
                    <Col xs={12} md={controlMd}>
                        {props.children}
                        {props.note && (
                            <div className="small d-flex fst-italic opacity-75 mt-1">
                                <span className="me-2 text-primary">*</span>
                                <span>{props.note}</span>
                            </div>
                        )}
                    </Col>
                </Row>
            </StrapGroup>
        );
    }

    if (groupType === "topLabel") {
        return (
            <StrapGroup {...ComponentProps(props, groupType)}>
                {hasLabel && (
                    <FormLabel
                        {...LabelProps(props)}
                        style={{ marginBottom: "var(--space-tight-padding)" }}
                    >
                        {props.label}
                    </FormLabel>
                )}
                {props.children}
                {props.note && (
                    <div className="small d-flex fst-italic opacity-75 mt-1">
                        <span className="me-2 text-primary">*</span>
                        <span>{props.note}</span>
                    </div>
                )}
            </StrapGroup>
        );
    }

    if (groupType === "switch") {
        return (
            <StrapGroup {...ComponentProps(props, groupType)}>
                {hasLabel && (
                    <FormLabel
                        {...LabelProps(props)}
                        style={{ marginBottom: "var(--space-tight-padding)" }}
                    >
                        {props.label}
                    </FormLabel>
                )}
                <div style={{ display: "flex", alignItems: "center", height: 38 }}>
                    {props.children}
                </div>
                {props.note && (
                    <div className="small d-flex fst-italic opacity-75 mt-1">
                        <span className="me-2 text-primary">*</span>
                        <span>{props.note}</span>
                    </div>
                )}
            </StrapGroup>
        );
    }

    if (groupType === "check" || groupType === "radio") {
        return (
            <StrapGroup {...ComponentProps(props, groupType)}>
                {props.children}
                {hasLabel && (
                    <FormLabel
                        {...LabelProps(props)}
                        style={{ marginLeft: "0.5rem" }}
                    >
                        {props.label}
                    </FormLabel>
                )}
                {props.note && (
                    <div className="small d-flex fst-italic opacity-75 mt-1">
                        <span className="me-2 text-primary">*</span>
                        <span>{props.note}</span>
                    </div>
                )}
            </StrapGroup>
        );
    }

    return (
        <StrapGroup {...ComponentProps(props, groupType)}>
            {props.note ? (
                <>
                    {hasLabel && (
                        <>
                            <Row>
                                <FormLabel
                                    {...LabelProps(props)}
                                    style={
                                        groupType === "floating"
                                            ? { position: "inherit" }
                                            : undefined
                                    }
                                >
                                    {props.label}
                                </FormLabel>
                            </Row>
                            <Row className="mb-1">
                                <div className="small d-flex fst-italic opacity-75">
                                    <span className="me-2 text-primary">*</span>
                                    <span>{props.note}</span>
                                </div>
                            </Row>
                        </>
                    )}
                    {props.children}
                </>
            ) : (
                <>
                    {groupType !== "floating" && hasLabel && (
                        <FormLabel {...LabelProps(props)}>{props.label}</FormLabel>
                    )}
                    {props.children}
                    {groupType === "floating" && hasLabel && (
                        <FormLabel {...LabelProps(props)}>{props.label}</FormLabel>
                    )}
                </>
            )}
        </StrapGroup>
    );
};